import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { Skeleton } from '@/components/ui';
import { syncLanguageFromSession } from '@/i18n';

/** All available PostgreSQL roles */
export type PostgresRole = 'employee' | 'full_admin' | 'account_manager' | 'sales_manager' | 'operations_manager' | 'quality_manager' | 'director' | 'csm';

/** All roles - used when any authenticated user with a role can access */
export const ALL_ROLES: PostgresRole[] = ['employee', 'full_admin', 'account_manager', 'sales_manager', 'operations_manager', 'quality_manager', 'director'];

/** Leadership team is a super-team: members bypass team-membership checks. */
export const LEADERSHIP_TEAM = 'Leadership';

/** Read team names from the session user object (set by web_api_server getSessionData). */
export function getUserTeamNames(user: { teams?: Array<{ name?: string }> } | null | undefined): string[] {
  return (user?.teams || []).map((t) => t?.name).filter((n): n is string => typeof n === 'string');
}

/** Returns true if the user is in the Leadership super-team. */
export function isLeadership(user: { teams?: Array<{ name?: string }> } | null | undefined): boolean {
  return getUserTeamNames(user).includes(LEADERSHIP_TEAM);
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** PostgreSQL roles required to access this route. User must have at least one of these roles. */
  requiredRoles?: PostgresRole[];
  /** Team names required to access this route. User must be in at least one. Leadership team always passes. */
  requiredTeams?: string[];
  /** Permission strings required (from user.permission_strings). full_admin/director bypass this check. */
  requiredPermissions?: string[];
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
  /** Custom unauthorized component */
  unauthorizedComponent?: React.ReactNode;
}

/**
 * A wrapper component that protects routes based on authentication and PostgreSQL roles
 *
 * - Redirects to /login if not authenticated
 * - Shows unauthorized message if user doesn't have any of the required PostgreSQL roles
 *
 * All authorization is based on PostgreSQL user_roles table. MongoDB admin field is ignored.
 */
export function ProtectedRoute({
  children,
  requiredRoles = ALL_ROLES,
  requiredTeams,
  requiredPermissions,
  loadingComponent,
  unauthorizedComponent,
}: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = useSession();
  const location = useLocation();

  // Sync i18n language with user session
  useEffect(() => {
    if (user?.language) {
      syncLanguageFromSession(user.language);
    }
  }, [user?.language]);

  // Show loading state while checking session
  if (isLoading) {
    return loadingComponent ?? <LoadingScreen />;
  }

  // Not authenticated - redirect to React login
  if (!isAuthenticated || !user) {
    const currentPath = location.pathname + location.search;
    return <Navigate to={`/login?url=${encodeURIComponent(currentPath)}`} replace />;
  }

  // Check PostgreSQL roles - user must have at least one of the required roles
  if (requiredRoles.length > 0) {
    const userRoles = user.postgres_roles || [];
    const hasRequiredRole = requiredRoles.some((role) => userRoles.includes(role));
    if (!hasRequiredRole) {
      return unauthorizedComponent ?? <UnauthorizedScreen />;
    }
  }

  // Check team membership. Leadership team always passes. full_admin role also bypasses.
  if (requiredTeams && requiredTeams.length > 0) {
    const userRoles = user.postgres_roles || [];
    if (!userRoles.includes('full_admin') && !isLeadership(user)) {
      const userTeamNames = getUserTeamNames(user);
      const hasRequiredTeam = requiredTeams.some((team) => userTeamNames.includes(team));
      if (!hasRequiredTeam) {
        return unauthorizedComponent ?? <UnauthorizedScreen />;
      }
    }
  }

  // Check permission strings (full_admin/director bypass this check, matching web_api_server deco.auth)
  if (requiredPermissions && requiredPermissions.length > 0) {
    const userRoles = user.postgres_roles || [];
    if (!userRoles.includes('full_admin') && !userRoles.includes('director')) {
      const userPerms = user.permission_strings || [];
      const hasAllPermissions = requiredPermissions.every((p) => userPerms.includes(p));
      if (!hasAllPermissions) {
        return unauthorizedComponent ?? <UnauthorizedScreen />;
      }
    }
  }

  return <>{children}</>;
}

/**
 * Loading screen shown while checking authentication
 */
function LoadingScreen({ message = 'Loading...' }: { message?: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="text-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
        <p className="mt-4 text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

/**
 * Unauthorized screen shown when user doesn't have required role
 */
function UnauthorizedScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="max-w-md text-center p-8 bg-white rounded-lg shadow-sm">
        <div className="text-6xl mb-4">🔒</div>
        <h1 className="text-2xl font-bold text-foreground mb-2">
          Access Denied
        </h1>
        <p className="text-muted-foreground mb-6">
          You don&apos;t have the required role to access this page.
        </p>
        <div className="flex gap-4 justify-center">
          <a
            href="/accounts"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Go to Accounts
          </a>
          <a
            href="/logout"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
          >
            Logout
          </a>
        </div>
      </div>
    </div>
  );
}
